import { computed, Injectable, signal } from '@angular/core';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import {
  Agency,
  AgencyKeys,
  Agent,
  AgentKeys,
  SupportedCollections,
  UploadFilePayload,
  UploadMediaPayloadKeys,
} from '@ag-common-lib/public-api';
import dxTreeList, {
  Node,
  RowDraggingAddEvent,
  RowDraggingChangeEvent,
  RowDraggingReorderEvent,
  RowPreparedEvent,
} from 'devextreme/ui/tree_list';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatest,
  filter,
  finalize,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  AgencyHierarchyManagerMode,
  HierarchyItem,
  MgaHierarchy,
  PreparedHierarchyChanges,
  TransferConfig,
  TransferLeadSelection,
} from './agency-hierarchy-management.model';
import { AgencyService, AuthService } from 'ag-common-svc/public-api';
import { adjustColumnWidth, getFillByLevel } from 'ag-common-svc/lib/utils/exceljs.util';
import { getAgentFullName } from 'ag-common-svc/lib/utils/agent-data.util';
import { Workbook, Worksheet } from 'exceljs';
import { DxFilterOperators } from '@ag-common-lib/lib';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import { LoadOptions } from 'devextreme/data';
import { CustomStore, DataSource } from 'devextreme/common/data';
import {
  AgentTeamTransfer,
  AgentTeamTransferKeys,
} from '@ag-common-lib/lib/models/functions/ship-document-to-elastic.model';
import { fileToUIBase64 } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.utils';
import { CloudFunctionsService } from 'ag-common-svc/lib/services/cloud-functions.service';
import { AgentTeamTransferElasticSearchService } from 'ag-common-svc/lib/services/elastic-search.services/agent-team-transfer-elastic-search.service';

@Injectable()
export class AgencyHierarchyManagementService {
  private static readonly DEBUG_EMPTY_LEAD_TRANSFER = true;
  private modeSig = signal<AgencyHierarchyManagerMode>(AgencyHierarchyManagerMode.default);
  private mgaSig = signal<Agency>(null);
  private transferConfigSig = signal<TransferConfig>(null);
  private originHierarchyRefreshSig = signal(0);
  private targetHierarchyRefreshSig = signal(0);
  // Saved manager/MGA updates kept in the UI until Elastic Search reflects the Firestore write.
  private persistedHierarchyChangesSig = signal<Record<string, Agent>>({});
  // Origin MGA id whose agents have finished loading. Used to hang the grids only on MGA switch, not on same-MGA refresh.
  private loadedOriginMgaIdSig = signal<string | null>(null);

  private isOriginMgaDataLoadingSig = signal(false);
  originMgaSig = computed(() => {
    const mga = this.mgaSig();
    const transferConfig = this.transferConfigSig();

    return transferConfig?.originMga ?? mga;
  });
  private originMga$ = toObservable(this.originMgaSig);
  private originMgaAgents$ = combineLatest({
    mga: this.originMga$,
    _refreshIndex: toObservable(this.originHierarchyRefreshSig),
  }).pipe(
    filter(({ mga }) => Boolean(mga)),
    switchMap(({ mga }) => {
      const mgaId = mga?.[AgencyKeys.agencyId];
      this.isOriginMgaDataLoadingSig.set(true);
      return from(this.getAgents(mgaId)).pipe(
        tap(agents => {
          this.loadedOriginMgaIdSig.set(mgaId);
          this.prunePersistedChanges(agents);
        }),
        catchError(() => of([] as Agent[])),
        finalize(() => {
          this.isOriginMgaDataLoadingSig.set(false);
        }),
      );
    }),
    shareReplay(1),
  );

  targetMgaSig = computed(() => {
    const transferConfig = this.transferConfigSig();

    return transferConfig?.targetMga;
  });
  private targetMga$ = toObservable(this.targetMgaSig);
  private targetMgaAgents$ = combineLatest({
    mga: this.targetMga$,
    _refreshIndex: toObservable(this.targetHierarchyRefreshSig),
  }).pipe(
    filter(({ mga }) => Boolean(mga)),
    switchMap(({ mga }) => {
      const mgaId = mga?.[AgencyKeys.agencyId];

      return from(this.getAgents(mgaId)).pipe(catchError(() => of([] as Agent[])));
    }),
    shareReplay(1),
  );
  hasOriginMgaSig = computed(() => {
    return !!this.originMgaSig();
  });
  originMgaGridTitleSig = computed(() => {
    const originMga = this.originMgaSig();
    if (!originMga) {
      return 'MGA not Selected';
    }
    const mgaName = originMga?.[AgencyKeys.name];

    return [mgaName, 'MGA Hierarchy'].filter(Boolean).join(' ');
  });
  originMgaNoDataTextSig = computed(() => {
    const originMga = this.originMgaSig();
    if (!originMga) {
      return 'MGA not Selected';
    }
    const agencyLead = originMga?.[AgencyKeys.agencyLead];

    return agencyLead ? 'No Lead Assigned' : '';
  });
  hasTargetMgaSig = computed(() => {
    const hasTargetMga = !!this.targetMgaSig();

    return hasTargetMga;
  });
  targetMgaGridTitleSig = computed(() => {
    const targetMga = this.targetMgaSig();
    if (!targetMga) {
      return 'MGA not Selected';
    }
    const mgaName = targetMga?.[AgencyKeys.name];

    return [mgaName, 'MGA Hierarchy'].filter(Boolean).join(' ');
  });
  targetMgaNoDataTextSig = computed(() => {
    const targetMga = this.targetMgaSig();
    if (!targetMga) {
      return 'MGA not Selected';
    }
    const agencyLead =
      this.targetHierarchySig()?.mgaLead?.agent?.[AgentKeys.p_agent_id] ?? targetMga?.[AgencyKeys.agencyLead];

    return agencyLead ? '' : 'No MGA Lead Assigned';
  });

  private hierarchyChangesTrigger$ = combineLatest({
    mode: toObservable(this.modeSig),
    transferConfig: toObservable(this.transferConfigSig),
  });

  private originHierarchy$: Observable<MgaHierarchy> = this.hierarchyChangesTrigger$.pipe(
    switchMap(({ mode, transferConfig }) => {
      return combineLatest({
        mga: this.originMga$,
        agents: this.originMgaAgents$,
        hierarchyChanges: this.hierarchyChanges$,
      }).pipe(
        map(({ mga, agents, hierarchyChanges }) => {
          return { mga, agents, hierarchyChanges, mode, transferConfig };
        }),
      );
    }),
    map(({ mode, transferConfig, mga, agents, hierarchyChanges }) => {
      return AgencyHierarchyManagementService.getHierarchy({
        mode,
        transferConfig,
        mga,
        agents,
        hierarchyChanges,
      });
    }),
    shareReplay(1),
  );

  private targetHierarchy$: Observable<MgaHierarchy> = this.hierarchyChangesTrigger$.pipe(
    switchMap(({ mode, transferConfig }) => {
      return combineLatest({
        mga: this.targetMga$,
        agents: this.targetMgaAgents$,
        hierarchyChanges: this.hierarchyChanges$,
      }).pipe(
        map(({ mga, agents, hierarchyChanges }) => {
          return { mga, agents, hierarchyChanges, mode, transferConfig };
        }),
      );
    }),
    map(({ mode, transferConfig, mga, agents, hierarchyChanges }) => {
      return AgencyHierarchyManagementService.getHierarchy({
        mode,
        transferConfig,
        mga,
        agents,
        hierarchyChanges,
      });
    }),
    shareReplay(1),
  );

  private originHierarchySig = toSignal(this.originHierarchy$);
  private targetHierarchySig = toSignal(this.targetHierarchy$);

  transferResolvedSig = computed(() => {
    const freeAgents = this.originHierarchySig()?.freeAgents;

    return !freeAgents?.length;
  });
  targetMgaLeadSelectionSig = computed<TransferLeadSelection | null>(() => {
    const targetMga = this.targetMgaSig();
    const existingTargetLead = targetMga?.[AgencyKeys.agencyLead];
    const currentTargetLead = this.targetHierarchySig()?.mgaLead?.agent?.[AgentKeys.p_agent_id];
    const targetMgaDbId = targetMga?.[BaseModelKeys.dbId];

    if (!targetMgaDbId || existingTargetLead || !currentTargetLead) {
      return null;
    }

    return {
      targetMgaDbId,
      targetMgaLeadAgentId: currentTargetLead,
    };
  });

  private agentsByAgentIdMapSig = computed(() => {
    const originAgentsByAgentIdMap = this.originHierarchySig()?.agentsByAgentIdMap ?? [];
    const targetAgentsByAgentIdMap = this.targetHierarchySig()?.agentsByAgentIdMap ?? [];

    return new Map([...originAgentsByAgentIdMap, ...targetAgentsByAgentIdMap]);
  });

  private hierarchyChangesSig = signal<Agent[][]>([], { equal: () => false });
  hierarchyChangesMapSig = computed<Record<string, Agent>>(() => {
    const hierarchyChangesMap = {};
    const defaultHierarchyChanges = this.transferConfigSig()?.defaultHierarchyChanges;
    const hierarchyChanges = this.hierarchyChangesSig();

    defaultHierarchyChanges?.forEach(changesBatch => {
      changesBatch?.forEach(hierarchyChange => {
        const agentDbID = hierarchyChange?.[BaseModelKeys.dbId];
        hierarchyChangesMap[agentDbID] = hierarchyChange;
      });
    });
    hierarchyChanges?.forEach(changesBatch => {
      changesBatch?.forEach(hierarchyChange => {
        const agentDbID = hierarchyChange?.[BaseModelKeys.dbId];
        hierarchyChangesMap[agentDbID] = hierarchyChange;
      });
    });

    return hierarchyChangesMap;
  });
  hasHierarchyChangesSig = computed(() => {
    const hierarchyChanges = this.hierarchyChangesSig();
    return !!hierarchyChanges?.length;
  });
  private effectiveHierarchyChangesMapSig = computed<Record<string, Agent>>(() => {
    return {
      ...this.persistedHierarchyChangesSig(),
      ...this.hierarchyChangesMapSig(),
    };
  });
  hierarchyChanges$ = toObservable(this.effectiveHierarchyChangesMapSig).pipe(shareReplay(1));

  protected agenciesMapByAgencyIdSig = toSignal(this.agencyService.agenciesMapByAgencyId$);

  public freeAgentsDataSourceSig = computed<Array<void> | DataSource | CustomStore>(() => {
    this.originHierarchySig();
    const selectedMga = this.originMgaSig();
    const selectedMgaId = selectedMga?.[AgencyKeys.agencyId];
    const isLoading = this.isOriginMgaDataLoadingSig();
    const isMgaDataReady = this.loadedOriginMgaIdSig() === selectedMgaId;

    if (isLoading && !isMgaDataReady) {
      return new CustomStore({ load: () => new Promise(() => {}) });
    }

    if (!selectedMga) {
      return [];
    }

    const dataSource = new DataSource({
      sort: [{ selector: `agent.${AgentKeys.p_agent_name}` }],
      store: new CustomStore({
        key: `agent.${BaseModelKeys.dbId}`,
        load: loadOptions => {
          return this.loadFreeAgents(loadOptions);
        },
      }),
    });

    return dataSource;
  });

  public originMgaHierarchyDataSourceSig = computed(() => {
    this.originHierarchySig();
    const selectedMga = this.originMgaSig();
    const selectedMgaId = selectedMga?.[AgencyKeys.agencyId];
    const isLoading = this.isOriginMgaDataLoadingSig();
    const isMgaDataReady = this.loadedOriginMgaIdSig() === selectedMgaId;

    if (isLoading && !isMgaDataReady) {
      return new CustomStore({ load: () => new Promise(() => {}) });
    }

    if (!selectedMga) {
      return [];
    }

    return new DataSource({
      sort: [{ selector: 'isShadowLead', desc: true }, { selector: `agent.${AgentKeys.p_agent_name}` }],
      store: new CustomStore({
        key: `agent.${BaseModelKeys.dbId}`,
        load: loadOptions => {
          return this.loadOriginMgaHierarchy(loadOptions);
        },
      }),
    });
  });

  public targetMgaHierarchyDataSourceSig = computed(() => {
    const selectedMga = this.targetMgaSig();
    this.targetHierarchySig();

    if (!selectedMga) {
      return [];
    }

    return new DataSource({
      sort: [{ selector: 'isShadowLead', desc: true }, { selector: `agent.${AgentKeys.p_agent_name}` }],
      store: new CustomStore({
        key: `agent.${BaseModelKeys.dbId}`,
        load: loadOptions => {
          return this.loadTargetMgaHierarchy(loadOptions);
        },
      }),
    });
  });

  constructor(
    private authService: AuthService,
    private agencyService: AgencyService,
    private cloudFunctionsService: CloudFunctionsService,
    private agentSearchService: AgentSearchService,
    private agentTeamTransferElasticSearchService: AgentTeamTransferElasticSearchService,
  ) {}

  setMode = this.modeSig.set;
  setTransferConfig = transferConfig => {
    this.hierarchyChangesSig.set([]);
    this.persistedHierarchyChangesSig.set({});
    this.transferConfigSig.set(transferConfig);
  };
  setMga = this.mgaSig.set;

  refreshOriginHierarchy = () => {
    this.originHierarchyRefreshSig.update(value => value + 1);
  };
  refreshTargetHierarchy = () => {
    this.targetHierarchyRefreshSig.update(value => value + 1);
  };

  refreshHierarchies = () => {
    this.refreshOriginHierarchy();
    this.refreshTargetHierarchy();
  };

  handleReorderMgaHierarchy = (e: RowDraggingReorderEvent<HierarchyItem>) => {
    const sourceDataAgent = e?.itemData?.agent;
    const sourceAgentDbId = sourceDataAgent?.[BaseModelKeys.dbId];
    const sourceAgentId = sourceDataAgent?.[AgentKeys.p_agent_id];
    const targetAgentId = this.getDropManagerId(e);

    if (!sourceDataAgent || !sourceAgentDbId || !targetAgentId) {
      return;
    }

    if (this.wouldCreateCycle(sourceAgentId, targetAgentId)) {
      return;
    }

    this.hierarchyChangesSig.update(changes => {
      return [
        ...changes,
        [
          Object.assign({}, sourceDataAgent, {
            [BaseModelKeys.dbId]: sourceAgentDbId,
            [AgentKeys.manager_id]: targetAgentId,
          }),
        ],
      ];
    });
  };

  handleDragChange = (e: RowDraggingChangeEvent<HierarchyItem>) => {
    const visibleRows = e.component.getVisibleRows();
    const target = visibleRows?.[e?.toIndex];
    const targetData = target?.data;
    const targetAgent = targetData?.agent;
    const isReordering = e.fromComponent === e.toComponent;
    const isEmptyTargetDrop = !target && !visibleRows?.length;
    const sourceDataAgent = e.itemData?.agent;
    const sourceAgentDbId = sourceDataAgent?.[BaseModelKeys.dbId];
    const sourceAgentId = sourceDataAgent?.[AgentKeys.p_agent_id];
    const targetAgentDbId = targetAgent?.[BaseModelKeys.dbId];

    if (isEmptyTargetDrop) {
      if (isReordering) {
        e.cancel = true;
      }
      return;
    }

    if (sourceAgentDbId && sourceAgentDbId === targetAgentDbId) {
      e.cancel = true;
      return;
    }

    if (e.dropInsideItem && !targetAgent?.[AgentKeys.is_manager]) {
      e.cancel = true;
      return;
    }

    if (!target?.level && !e?.dropInsideItem) {
      e.cancel = true;
      return;
    }

    const newManagerId = this.getDropManagerId(e);
    if (this.wouldCreateCycle(sourceAgentId, newManagerId)) {
      e.cancel = true;
      return;
    }

    if (!isReordering) {
      return;
    }

    const sourceManagerId = sourceDataAgent?.[AgentKeys.manager_id];
    const parentKeys = new Set<string>();
    this.getHierarchyChain(target?.node, parentKeys);

    if (sourceAgentDbId && parentKeys.has(sourceAgentDbId)) {
      e.cancel = true;
      return;
    }

    if (e.dropInsideItem && sourceManagerId === targetAgent?.[AgentKeys.p_agent_id]) {
      e.cancel = true;
      return;
    }

    const targetParentAgent = target?.node?.parent?.data?.agent;
    if (!e.dropInsideItem && sourceManagerId === targetParentAgent?.[AgentKeys.p_agent_id]) {
      e.cancel = true;
    }
  };

  revertChange = () => {
    this.hierarchyChangesSig.update(changes => {
      const result = changes.slice(0, -1);

      return result;
    });
  };

  clearChanges = () => {
    this.hierarchyChangesSig.set([]);
    this.persistedHierarchyChangesSig.set({});
  };

  commitChanges = () => {
    const pendingChanges = this.hierarchyChangesSig();
    if (!pendingChanges?.length) {
      return;
    }

    this.persistedHierarchyChangesSig.update(persisted => {
      const nextPersistedChanges = { ...persisted };

      pendingChanges.forEach(changesBatch => {
        changesBatch?.forEach(hierarchyChange => {
          const agentDbId = hierarchyChange?.[BaseModelKeys.dbId];
          if (!agentDbId) {
            return;
          }

          nextPersistedChanges[agentDbId] = hierarchyChange;
        });
      });

      return nextPersistedChanges;
    });
    this.hierarchyChangesSig.set([]);
  };

  private debugTransferState = (label: string, payload: Record<string, unknown>) => {
    if (!AgencyHierarchyManagementService.DEBUG_EMPTY_LEAD_TRANSFER) {
      return;
    }

    console.log(`[AgencyHierarchyManagement] ${label}\n${JSON.stringify(payload, null, 2)}`);
  };

  prepareAddAgents = (mga: Agency, e: RowDraggingAddEvent<HierarchyItem>): PreparedHierarchyChanges | null => {
    const fromComponent = e.fromComponent as dxTreeList;
    const sourceDataAgent = e.itemData?.agent;
    const sourceAgentDbId = sourceDataAgent?.[BaseModelKeys.dbId];
    const sourceAgentId = sourceDataAgent?.[AgentKeys.p_agent_id];

    if (!sourceDataAgent || !sourceAgentDbId) {
      return null;
    }

    const sourceNode = fromComponent?.getNodeByKey(sourceAgentDbId);
    const targetAgentId = this.getDropManagerId(e);

    if (this.wouldCreateCycle(sourceAgentId, targetAgentId)) {
      return null;
    }

    const mgaId = mga?.[AgencyKeys.agencyId];
    const agencyIds = {
      [AgentKeys.p_mga_id]: mgaId,
    };
    const sourceChanges = Object.assign({}, sourceDataAgent, agencyIds, {
      [AgentKeys.manager_id]: targetAgentId,
    });
    const mode = this.modeSig();
    const transferConfig = this.transferConfigSig();
    const transferedAgency = transferConfig?.transferedAgency;
    const transferedAgencyId = transferedAgency?.[AgencyKeys.agencyId];

    if (mode === AgencyHierarchyManagerMode.agentTransfer) {
      agencyIds[AgentKeys.p_agency_id] = null;
    }

    if (mode === AgencyHierarchyManagerMode.agencyTransfer) {
      const sourceAgentMgaId = sourceDataAgent?.[AgentKeys.p_mga_id];
      const sourceAgentAgencyId = sourceDataAgent?.[AgentKeys.p_agency_id];

      (sourceAgentAgencyId !== transferedAgencyId || sourceAgentMgaId === mgaId) &&
        (sourceChanges[AgentKeys.p_agency_id] = null);
    }

    const changes = [sourceChanges];

    const teammates = [];
    const getTeammates = (children: Node[], visitedNodes = new Set<Node>()) => {
      if (!Array.isArray(children)) {
        return;
      }
      children?.forEach(child => {
        if (!child || visitedNodes.has(child)) {
          return;
        }
        visitedNodes.add(child);

        const childData = child?.data;
        const childAgentData = childData?.agent;

        if (mgaId !== childAgentData?.[AgentKeys.p_mga_id]) {
          teammates.push(childAgentData);
        }
        getTeammates(child?.children, visitedNodes);
      });
    };

    getTeammates(sourceNode?.children);

    teammates.forEach(teammate => {
      const teammateChanges = Object.assign({}, teammate, agencyIds);

      if (mode === AgencyHierarchyManagerMode.agencyTransfer) {
        const teammateMgaId = teammate?.[AgentKeys.p_mga_id];
        const teammateAgencyId = teammate?.[AgentKeys.p_agency_id];

        (teammateAgencyId !== transferedAgencyId || teammateMgaId === mgaId) &&
          (teammateChanges[AgentKeys.p_agency_id] = null);
      }

      changes.push(teammateChanges);
    });

    this.debugTransferState('prepareAddAgents', {
      mode,
      targetMgaId: mgaId,
      source: {
        dbId: sourceChanges?.[BaseModelKeys.dbId],
        agentId: sourceChanges?.[AgentKeys.p_agent_id],
        mgaId: sourceChanges?.[AgentKeys.p_mga_id],
        agencyId: sourceChanges?.[AgentKeys.p_agency_id],
        managerId: sourceChanges?.[AgentKeys.manager_id],
        isManager: sourceChanges?.[AgentKeys.is_manager],
      },
      teammates: changes.slice(1).map(change => {
        return {
          dbId: change?.[BaseModelKeys.dbId],
          agentId: change?.[AgentKeys.p_agent_id],
          mgaId: change?.[AgentKeys.p_mga_id],
          agencyId: change?.[AgentKeys.p_agency_id],
          managerId: change?.[AgentKeys.manager_id],
          isManager: change?.[AgentKeys.is_manager],
        };
      }),
    });

    return {
      changes,
      sourceAgentName: sourceDataAgent?.[AgentKeys.p_agent_name] ?? 'This agent',
    };
  };

  applyPreparedChanges = (preparedChanges: PreparedHierarchyChanges | null) => {
    if (!preparedChanges?.changes?.length) {
      return;
    }

    this.debugTransferState('applyPreparedChanges', {
      changes: preparedChanges?.changes?.map(change => {
        return {
          dbId: change?.[BaseModelKeys.dbId],
          agentId: change?.[AgentKeys.p_agent_id],
          mgaId: change?.[AgentKeys.p_mga_id],
          agencyId: change?.[AgentKeys.p_agency_id],
          managerId: change?.[AgentKeys.manager_id],
          isManager: change?.[AgentKeys.is_manager],
        };
      }),
    });

    this.hierarchyChangesSig.update(hierarchyChanges => {
      return [...hierarchyChanges, preparedChanges.changes];
    });
  };

  onMgaHierarchyRowPrepared = (e: RowPreparedEvent) => {
    const transferConfig = this.transferConfigSig();
    const rowData = e.data;
    const isRowAgentShadowLead = rowData?.isShadowLead;
    const rowAgentData = rowData?.agent;
    const rowAgentDbId = rowAgentData?.[BaseModelKeys.dbId];
    const hierarchyChangesMap = this.hierarchyChangesMapSig();
    const transferedAgent = transferConfig?.transferedAgent;
    const chain = this.getHierarchyChain(e?.node);
    const changesKeys = Object.keys(hierarchyChangesMap);
    const isLeadAdded = changesKeys?.length && changesKeys.some(key => chain.has(key));
    const rowElement = e.rowElement;

    if (hierarchyChangesMap[rowAgentDbId] || isLeadAdded) {
      rowElement?.classList?.add('agency-hierarchy-management__added-row');
    }

    if (transferedAgent?.[BaseModelKeys.dbId] === rowAgentDbId) {
      rowElement?.classList?.add('agency-hierarchy-management__transfered-row');
    }

    if (isRowAgentShadowLead) {
      rowElement?.classList?.add('agency-hierarchy-management__shadow-lead-row');
    }
  };

  private getHierarchyChain = (
    node,
    set = new Set<string>(),
    visitedNodes = new Set<unknown>(),
  ): Set<string> => {
    const parent = node?.parent;
    const parentKey = parent?.key;

    if (!parent || visitedNodes.has(parent)) {
      return set;
    }
    visitedNodes.add(parent);

    if (parentKey != null) {
      set.add(parentKey);
    }

    return this.getHierarchyChain(parent, set, visitedNodes);
  };

  private getDropManagerId = (
    e:
      | RowDraggingAddEvent<HierarchyItem>
      | RowDraggingChangeEvent<HierarchyItem>
      | RowDraggingReorderEvent<HierarchyItem>,
  ): string | undefined => {
    const visibleRows = e.component.getVisibleRows();
    const target = visibleRows?.[e?.toIndex];
    const targetAgent = target?.data?.agent;
    const parentAgent = target?.node?.parent?.data?.agent;

    if (e?.dropInsideItem) {
      return targetAgent?.[AgentKeys.p_agent_id];
    }

    return parentAgent?.[AgentKeys.p_agent_id];
  };

  private wouldCreateCycle = (sourceAgentId?: string, newManagerId?: string): boolean => {
    if (!sourceAgentId || !newManagerId) {
      return false;
    }

    if (sourceAgentId === newManagerId) {
      return true;
    }

    const agentsByAgentIdMap = this.agentsByAgentIdMapSig();
    const visited = new Set<string>();
    let currentId = newManagerId;

    while (currentId) {
      if (currentId === sourceAgentId) {
        return true;
      }
      if (visited.has(currentId)) {
        return true;
      }
      visited.add(currentId);

      const manager = agentsByAgentIdMap.get(currentId);
      currentId = manager?.[AgentKeys.manager_id];
    }

    return false;
  };
  /**
   * Prune persisted changes that no longer exist in the agents array.
   * This is necessary when agents are refreshed and the persisted changes are no longer valid.
   * The agent stays on the right even if Elastic is still stale
   */
  private prunePersistedChanges = (agents: Agent[]) => {
    const persisted = this.persistedHierarchyChangesSig();
    const persistedKeys = Object.keys(persisted);
    if (!persistedKeys.length || !agents?.length) {
      return;
    }

    const agentsByDbId = new Map(agents.map(agent => [agent?.[BaseModelKeys.dbId], agent]));
    let hasChanges = false;
    const nextPersistedChanges = { ...persisted };

    persistedKeys.forEach(dbId => {
      const agent = agentsByDbId.get(dbId);
      if (!agent) {
        return;
      }

      const persistedChange = persisted[dbId];
      const isManagerSynced =
        (agent?.[AgentKeys.manager_id] || '') === (persistedChange?.[AgentKeys.manager_id] || '');
      const isMgaSynced = (agent?.[AgentKeys.p_mga_id] || '') === (persistedChange?.[AgentKeys.p_mga_id] || '');
      if (!isManagerSynced || !isMgaSynced) {
        return;
      }

      delete nextPersistedChanges[dbId];
      hasChanges = true;
    });

    if (!hasChanges) {
      return;
    }

    this.persistedHierarchyChangesSig.set(nextPersistedChanges);
  };

  getTeamListBlob = async workSheetName => {
    const workbook = new Workbook();
    const agenciesMapByAgencyId = this.agenciesMapByAgencyIdSig();
    const worksheet: Worksheet = workbook.addWorksheet(workSheetName?.replace(/\*|\?|\:|\/|\\|\[|\]/g, ''));
    const transferConfig = this.transferConfigSig();
    const transferedAgentDbId = transferConfig?.transferedAgent?.[BaseModelKeys.dbId];
    const agents = await lastValueFrom(this.originMgaAgents$.pipe(filter(Boolean), take(1)));
    const originHierarchy = AgencyHierarchyManagementService.getHierarchy({
      mode: this.modeSig(),
      transferConfig,
      agents,
      mga: this.originMgaSig(),
    });
    const agentsByAgentIdMap = originHierarchy?.agentsByAgentIdMap;

    worksheet.columns = [
      { header: 'Email', key: AgentKeys.p_email },
      { header: 'Agent First Name', key: AgentKeys.p_agent_first_name },
      { header: 'Agent Last Name', key: AgentKeys.p_agent_last_name },
      { header: 'MGA Id', key: AgentKeys.p_mga_id },
      { header: 'MGA Name', key: 'mgaName' },
      { header: 'Agency Id', key: AgentKeys.p_agency_id },
      { header: 'Agency Name', key: 'agencyName' },
      { header: 'Agent Id', key: AgentKeys.p_agent_id },
      { header: 'Team Lead Id', key: AgentKeys.manager_id },
      { header: 'Team Lead', key: 'teamLeadName' },
      { header: 'Is Team Lead', key: AgentKeys.is_manager },
    ];

    const addRows = (items: HierarchyItem[], outlineLevel = 0) => {
      if (!Array.isArray(items)) {
        return;
      }

      const addRow = (item: HierarchyItem) => {
        const rowData: Agent & { teamLeadName?: string; mgaName?: string; agencyName?: string } = Object.assign(
          {},
          item?.agent,
        );
        const agentDbId = rowData?.[BaseModelKeys.dbId];
        const managerId = rowData?.[AgentKeys.manager_id];
        const mgaId = rowData?.[AgentKeys.p_mga_id];
        const agencyId = rowData?.[AgentKeys.p_agency_id];

        if (transferedAgentDbId && agentDbId === transferedAgentDbId) {
          return null;
        }

        const manager = agentsByAgentIdMap?.get(managerId);
        if (manager) {
          const fullName = getAgentFullName(manager);
          rowData.teamLeadName = fullName;
        }

        const mga = agenciesMapByAgencyId?.get(mgaId);
        if (mga) {
          rowData.mgaName = mga?.[AgencyKeys.name];
        }

        const agency = agenciesMapByAgencyId?.get(agencyId);
        if (agency) {
          rowData.agencyName = agency?.[AgencyKeys.name];
        }

        const row = worksheet.addRow(rowData);
        row.outlineLevel = outlineLevel;

        row.eachCell(cell => {
          cell.fill = getFillByLevel(outlineLevel);
        });

        if (item?.teamMembers?.length) {
          const childFill = getFillByLevel(outlineLevel + 1) as any;

          row.eachCell(cell => {
            cell.font = {
              bold: true,
            };
            cell.border = {
              bottom: { style: 'thick', color: childFill.fgColor },
            };
          });
        }

        return row;
      };

      items?.forEach(item => {
        const row = addRow(item);

        addRows(item?.teamMembers, row ? outlineLevel + 1 : outlineLevel);
      });
    };

    addRows(originHierarchy?.freeAgents, 0);
    adjustColumnWidth(worksheet);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);

    return { url, blob, teamSize: originHierarchy?.freeAgentSize, workbook };
  };

  getHierarchyChangesBlob = async () => {
    const workbook = new Workbook();
    const hierarchyChangesMap = this.hierarchyChangesMapSig();
    const agenciesMapByAgencyId = this.agenciesMapByAgencyIdSig();
    const worksheet: Worksheet = workbook.addWorksheet('Transfer Results');
    const agentsByAgentIdMap = this.agentsByAgentIdMapSig();

    worksheet.columns = [
      { header: 'Firestore DbId', key: BaseModelKeys.dbId },
      { header: 'Email', key: AgentKeys.p_email },
      { header: 'Agent First Name', key: AgentKeys.p_agent_first_name },
      { header: 'Agent Last Name', key: AgentKeys.p_agent_last_name },
      { header: 'MGA Id', key: AgentKeys.p_mga_id },
      { header: 'MGA Name', key: 'mgaName' },
      { header: 'Team Lead Id', key: AgentKeys.manager_id },
      { header: 'Team Lead', key: 'teamLeadName' },
      { header: 'Agency Id' },
    ];

    Object.values(hierarchyChangesMap ?? {}).forEach(change => {
      const rowData: Agent & { teamLeadName?: string; mgaName?: string; agencyName?: string } = Object.assign(
        {},
        change,
      );

      const managerId = rowData?.[AgentKeys.manager_id];
      const mgaId = rowData?.[AgentKeys.p_mga_id];

      const manager = agentsByAgentIdMap?.get(managerId);
      if (manager) {
        const fullName = getAgentFullName(manager);
        rowData.teamLeadName = fullName;
      }

      const mga = agenciesMapByAgencyId?.get(mgaId);
      if (mga) {
        rowData.mgaName = mga?.[AgencyKeys.name];
      }

      worksheet.addRow(rowData);
    });

    adjustColumnWidth(worksheet);

    const buffer = await workbook.csv.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });

    return blob;
  };

  logHierarchyChanges = async ({
    workSheetName,
    originMgaId,
    targetMgaId,
    teamBlob,
  }: {
    workSheetName: string;
    originMgaId: string;
    targetMgaId?: string;
    teamBlob: Blob;
  }) => {
    const hierarchyChangesMap = this.hierarchyChangesMapSig();
    const hierarchyChangesBlob = await this.getHierarchyChangesBlob();
    const [hierarchySourceKey, changeSourceKey] = await Promise.all([
      this.uploadSourceFile(`${workSheetName} Hierarchy (${new Date().toLocaleDateString('en')}).xlsx`, teamBlob),
      this.uploadSourceFile(
        `${workSheetName} Changes (${new Date().toLocaleDateString('en')}).csv`,
        hierarchyChangesBlob,
      ),
    ]);
    const loggedInAgent = await firstValueFrom(this.authService.loggedInAgent$);
    const agentTransferResult: AgentTeamTransfer = {
      [BaseModelKeys.createdDate]: new Date(),
      [BaseModelKeys.createdBy]: loggedInAgent?.[AgentKeys.uid] ?? null,
      [BaseModelKeys.createdByEmail]: loggedInAgent?.[AgentKeys.p_email] ?? null,
      [BaseModelKeys.createdByAgentDbId]: loggedInAgent?.[BaseModelKeys.dbId] ?? null,
      [AgentTeamTransferKeys.mode]: this.modeSig(),
      [AgentTeamTransferKeys.originMgaId]: originMgaId,
      [AgentTeamTransferKeys.hierarchyWasabiPath]: hierarchySourceKey,
      [AgentTeamTransferKeys.changesWasabiPath]: changeSourceKey,
      [AgentTeamTransferKeys.involvedAgentsDbIds]: Object.keys(hierarchyChangesMap ?? {}),
    };

    if (targetMgaId) {
      agentTransferResult[AgentTeamTransferKeys.targetMgaId] = targetMgaId;
    }

    await this.agentTeamTransferElasticSearchService.shipDocumentToElastic(agentTransferResult);
  };

  static getHierarchy = ({
    mode,
    transferConfig,
    mga,
    agents,
    hierarchyChanges = {},
  }: {
    mga: Agency;
    agents: Agent[];
    mode: AgencyHierarchyManagerMode;
    transferConfig: TransferConfig;
    hierarchyChanges?: Record<string, Agent>;
  }): MgaHierarchy => {
    const normalizeMgaAgents = () => {
      const mgaLeadId = mga?.[AgencyKeys.agencyLead];
      const mgaId = mga?.[AgencyKeys.agencyId];
      const freeAgents: HierarchyItem[] = [];
      const agentsByAgentIdMap = new Map<string, Agent>();
      const hierarchyMap = new Map<string, HierarchyItem[]>();
      const mgaLead: HierarchyItem = {
        agent: null,
        teamMembers: [],
      };

      if (mode === AgencyHierarchyManagerMode.agentTransfer) {
        mgaLead.isShadowLead = true;
        mgaLead.agent = {
          [AgentKeys.p_agent_name]: 'MGA Lead (To Be Transferred)',
          p_agent_id: null,
          is_manager: true,
        };
      }
      // const transferedAgencyLead: HierarchyItem = {
      //   agent: null,
      //   teamMembers: [],
      // };
      const transferedAgencyAgentsMap = new Map<string, HierarchyItem>();
      const addedAgentsSet = new Set();

      // const transferConfig = this.transferConfigSig();
      const transferedAgent: Agent = Object.assign({}, transferConfig?.transferedAgent);
      const transferedAgentDbId = transferedAgent?.[BaseModelKeys.dbId];
      const transferedAgentId = transferedAgent?.[AgentKeys.p_agent_id];
      const transferedAgency: Agency = Object.assign({}, transferConfig?.transferedAgency);
      const transferedAgencyId = transferedAgency?.[AgencyKeys.agencyId];
      const transferedAgencyLeadId = transferedAgency?.[AgencyKeys.agencyLead];

      if (hierarchyChanges?.[transferedAgentDbId]) {
        Object.assign(transferedAgent, hierarchyChanges[transferedAgentDbId]);
      }

      const transferedAgentManagerId = transferedAgent?.[AgentKeys.manager_id];

      if (
        mode === AgencyHierarchyManagerMode.agentTransfer &&
        mgaId === transferConfig?.originMga?.agency_id &&
        !transferedAgentManagerId &&
        transferConfig?.transferedAgent &&
        !hierarchyChanges?.[transferedAgentDbId]
      ) {
        freeAgents.push({ agent: transferedAgent, teamMembers: [] });
      }

      const normalizeAgent = item => {
        const agent = Object.assign({}, item);
        const agentDbId = agent?.[BaseModelKeys.dbId];

        const hasTransfered = hierarchyChanges?.[agentDbId];

        if (addedAgentsSet.has(agentDbId)) {
          return;
        }
        addedAgentsSet.add(agentDbId);

        // Apply hierarchy changes (if any) to the agent.
        if (hasTransfered) {
          Object.assign(agent, hierarchyChanges[agentDbId]);
        }

        const agentMGAId = agent?.[AgentKeys.p_mga_id];
        const agentId = agent?.[AgentKeys.p_agent_id] ?? null;
        const agencyId = agent?.[AgentKeys.p_agency_id];

        agentsByAgentIdMap.set(agentId, agent);

        if (!agentMGAId || agentMGAId !== mgaId) {
          return;
        }

        // If MGA Lead detected, assign it.
        if (!!mgaLeadId && mgaLeadId === agentId && mgaLeadId !== transferedAgentId) {
          mgaLead.agent = agent;
          mgaLead.isShadowLead = false;
          return;
        }

        // If transfered Agency Lead detected, assign it.
        if (!hasTransfered && !!transferedAgencyLeadId && transferedAgencyLeadId === agentId) {
          freeAgents.push({ agent, teamMembers: [] });
          return;
        }

        const managerId = agent?.[AgentKeys.manager_id];

        /**
         * Agent without a manager.
         * In `default` mode, such agents are considered "free agents".
         */
        const isFreeAgent = !managerId;
        if (mode === AgencyHierarchyManagerMode.default && isFreeAgent) {
          freeAgents.push({ agent, teamMembers: [] });
          return;
        }

        const isTargetMGA = mgaId === transferConfig?.targetMga?.agency_id;
        if (mode === AgencyHierarchyManagerMode.agentTransfer && isTargetMGA && isFreeAgent) {
          freeAgents.push({ agent, teamMembers: [] });
          return;
        }

        /**
         * Agents who are still assigned to the transferred agent as their manager.
         *
         * Since the transferred agent already has a new team lead,
         * his old direct reports no longer belong under him.
         * In `agentTransfer` mode, these agents are treated as temporary "free agents"
         * until they get reassigned to the correct manager.
         */
        const isTransferedAgentTeam = !!transferedAgentManagerId && managerId === transferedAgentId && !hasTransfered;
        if (mode === AgencyHierarchyManagerMode.agentTransfer && isTransferedAgentTeam) {
          freeAgents.push({ agent, teamMembers: [] });
          return;
        }

        const isBelongTransferedAgency = !hasTransfered && !!transferedAgencyId && transferedAgencyId === agencyId;
        if (mode === AgencyHierarchyManagerMode.agencyTransfer && isBelongTransferedAgency && isFreeAgent) {
          freeAgents.push({ agent, teamMembers: [] });
          return;
        }

        if (!managerId && !hierarchyChanges[agentDbId]) {
          return;
        }

        if (mode === AgencyHierarchyManagerMode.agencyTransfer && isBelongTransferedAgency) {
          transferedAgencyAgentsMap.set(agentDbId, { agent, teamMembers: [] });
        }

        if (!hierarchyMap.has(managerId)) {
          hierarchyMap.set(managerId, []);
        }

        hierarchyMap.get(managerId).push({ agent, teamMembers: [] });
      };

      agents.forEach(normalizeAgent);

      Object.values(hierarchyChanges).forEach(normalizeAgent);

      return {
        freeAgents,
        hierarchyMap,
        mgaLead,
        agentsByAgentIdMap,
        transferedAgencyAgentsMap,
      };
    };

    const { freeAgents, agentsByAgentIdMap, hierarchyMap, mgaLead, transferedAgencyAgentsMap } = normalizeMgaAgents();

    let freeAgentSize = 0;
    const buildHierarchy = (lead: HierarchyItem, shouldCount?: boolean, visited = new Set<string>()) => {
      const agent = lead?.agent;
      const agentDbId = agent?.[BaseModelKeys.dbId];
      const agentId = agent?.[AgentKeys.p_agent_id];

      if (agentId && visited.has(agentId)) {
        return;
      }
      if (agentId) {
        visited.add(agentId);
      }

      lead.teamMembers = [];

      transferedAgencyAgentsMap.delete(agentDbId);

      if (shouldCount) {
        freeAgentSize += 1;
      }

      const team = hierarchyMap.get(agentId);
      if (!team?.length) {
        return;
      }

      team.forEach(teamMember => {
        const teamMemberId = teamMember?.agent?.[AgentKeys.p_agent_id];
        if (teamMemberId && visited.has(teamMemberId)) {
          return;
        }

        lead.teamMembers.push(teamMember);
        buildHierarchy(teamMember, shouldCount, visited);
      });
    };

    freeAgents.forEach(freeManager => {
      buildHierarchy(freeManager, true);
    });

    transferedAgencyAgentsMap.forEach(hierarchyItem => {
      buildHierarchy(hierarchyItem, true);
      freeAgents.push(hierarchyItem);
    });

    const structuredAgents: HierarchyItem[] = [];

    // if (mgaLead?.isShadowLead) {
    //   if (!hierarchyMap.has(null)) {
    //     hierarchyMap.set(null, []);
    //   }

    //   hierarchyMap.get(null).push(...freeAgents);
    // }

    if (mgaLead?.agent) {
      structuredAgents.push(mgaLead);

      buildHierarchy(mgaLead);

      if (mgaLead?.isShadowLead) {
        mgaLead.teamMembers.push(...freeAgents);
      }
    }

    return {
      mgaLead,
      agentsByAgentIdMap,
      freeAgents,
      freeAgentSize,
      hierarchyMap,
      structuredAgents,
    };
  };

  private async uploadSourceFile(fileName, sourceFile: Blob): Promise<string> {
    const contentType = sourceFile.type;
    const base64 = await fileToUIBase64(sourceFile)
      .then(fileBase64 => {
        return fileBase64.split(',')[1];
      })
      .catch(() => {
        return null;
      });

    if (!base64) {
      return null;
    }
    const payload: UploadFilePayload = {
      [UploadMediaPayloadKeys.filePath]: SupportedCollections.agentTeamTransfer,
      [UploadMediaPayloadKeys.mediaData]: base64,
      [UploadMediaPayloadKeys.contentType]: contentType,
      [UploadMediaPayloadKeys.metadata]: {
        fileName,
      },
    };

    return this.cloudFunctionsService.uploadFile(payload).catch(() => {
      return null;
    });
  }

  private loadFreeAgents = async (loadOptions: LoadOptions) => {
    const observable = this.originHierarchy$.pipe(
      map(hierarchy => {
        return hierarchy?.freeAgents;
      }),
      take(1),
    );
    const response = await lastValueFrom(observable);

    return response ?? [];
  };

  private loadOriginMgaHierarchy = async (loadOptions: LoadOptions) => {
    const observable = this.originHierarchy$.pipe(
      map(hierarchy => hierarchy?.structuredAgents),
      take(1),
    );

    const response = await lastValueFrom(observable);

    return response ?? [];
  };

  private loadTargetMgaHierarchy = async (loadOptions: LoadOptions) => {
    const observable = this.targetHierarchy$.pipe(
      map(hierarchy => hierarchy?.structuredAgents),
      take(1),
    );

    const response = await lastValueFrom(observable);

    return response ?? [];
  };

  private getAgents = (mgaId: string) => {
    const filter: any = [[AgentKeys.p_mga_id, DxFilterOperators.equal, mgaId]];
    const transferedAgent = this.transferConfigSig()?.transferedAgent;

    if (this.transferConfigSig()) {
      filter.push(DxFilterOperators.and, [
        BaseModelKeys.dbId,
        DxFilterOperators.doNotEqual,
        transferedAgent?.[BaseModelKeys.dbId],
      ]);
    }

    return this.agentSearchService
      .search({
        filter,
        userData: { isLoadingAll: true },
      })
      .then(response => {
        return response?.data?.map(agent => {
          const fullNameWithEmail = getAgentFullName(agent);

          return Object.assign({}, agent, { [AgentKeys.p_agent_name]: fullNameWithEmail });
        });
      });
  };
}
